import React, { useState, useEffect, useRef } from "react";
import api from "utils/axiosInstance";

const TAILWIND_SCRIPT_ATTR = "data-tailwind-cdn";
const TAILWIND_SCRIPT_SELECTOR =
  'script[data-tailwind-cdn="true"], script#tailwind-cdn';

const TailwindInjector = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const removeTailwindNodes = () => {
      document
        .querySelectorAll(TAILWIND_SCRIPT_SELECTOR)
        .forEach((node) => node.remove());
      document.querySelectorAll("style").forEach((node) => {
        if (node.textContent && node.textContent.includes("--tw-")) {
          node.remove();
        }
      });
      document.querySelectorAll("link[rel='stylesheet']").forEach((node) => {
        if (node.href && node.href.includes("tailwind")) {
          node.remove();
        }
      });
      delete window.tailwind;
    };

    const existingScript = document.querySelector(TAILWIND_SCRIPT_SELECTOR);

    if (window.tailwind && existingScript) {
      setLoaded(true);
      return () => {
        removeTailwindNodes();
      };
    }

    if (existingScript) {
      const handleLoad = () => setLoaded(true);
      existingScript.addEventListener("load", handleLoad);
      return () => {
        existingScript.removeEventListener("load", handleLoad);
        removeTailwindNodes();
      };
    }

    const script = document.createElement("script");
    script.id = "tailwind-cdn";
    script.src = "https://cdn.tailwindcss.com";
    script.async = true;
    script.setAttribute(TAILWIND_SCRIPT_ATTR, "true");
    const handleLoad = () => {
      setLoaded(true);
    };
    script.addEventListener("load", handleLoad);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      removeTailwindNodes();
    };
  }, []);

  if (!loaded)
    return (
      <div className="flex items-center justify-center min-h-screen font-sans">
        Loading Styles...
      </div>
    );

  return children;
};

const MESSAGES = {
  instruction1:
    "Welcome to the Stroop Task! <br><br>In this task, you will see color names (red, green, blue, yellow) in different \"ink\" colors. <br><br><p class='text-center mt-8'>Press any key for instructions...</p>",
  instruction2:
    "Your task is to identify the INK COLOR of the word, ignoring the word itself. <br><br>You will respond to the ink color, and press the associated arrow. <br><br><strong><span class='text-red-500'>⬅️ (Left Arrow)</span></strong> for <span class='text-red-500'>RED</span><br><strong><span class='text-green-500'>⬆️ (Up Arrow)</span></strong> for <span class='text-green-500'>GREEN</span><br><strong><span class='text-blue-500'>⬇️ (Down Arrow)</span></strong> for <span class='text-blue-500'>BLUE</span><br><strong><span class='text-yellow-400'>➡️ (Right Arrow)</span></strong> for <span class='text-yellow-400'>YELLOW</span><br><br>Try to be as fast and as accurate as possible. <br><br><p class='text-center mt-8'>Press any key to continue instructions.</p>",
  instruction3_examples: `
      <p>Here are some examples:</p>
      <div class="flex flex-col items-center my-2">
          <span class="text-5xl font-bold text-red-500">YELLOW</span>
          <span class="text-4xl mt-1">⬅️</span>
      </div>
      <div class="flex flex-col items-center my-2">
          <span class="text-5xl font-bold text-green-500">BLUE</span>
          <span class="text-4xl mt-1">⬆️</span>
      </div>
      <div class="flex flex-col items-center my-2">
          <span class="text-5xl font-bold text-yellow-400">YELLOW</span>
          <span class="text-4xl mt-1">➡️</span>
      </div>
  `,
  instruction3_text:
    "<p class='mt-4'>It can be difficult, because the name and the ink color are different (except for yellow in the example above). So concentrate and ignore the meaning of the color words, instead, look at the ink color. <strong>This task will take about 2 minutes.</strong> Try to get as many correct as possible. Your response times and accuracy will be reviewed. Most people average about 85% accuracy.</p><p class='text-center mt-4'>Press any key to start...</p>",
  endTask: "<p class='text-2xl text-center'>Task Complete!</p>",
};

const RESPONSE_MAP = {
  arrowleft: { colorName: "red", cssClass: "text-red-500" },
  arrowup: { colorName: "green", cssClass: "text-green-500" },
  arrowdown: { colorName: "blue", cssClass: "text-blue-500" },
  arrowright: { colorName: "yellow", cssClass: "text-yellow-400" },
};

const STROOP_TABLE_RAW = [
  { stimulusId: "yellowyellow", responseKey: "arrowright", isCongruent: true },
  { stimulusId: "yellowgreen", responseKey: "arrowup", isCongruent: false },
  { stimulusId: "yellowblue", responseKey: "arrowdown", isCongruent: false },
  { stimulusId: "yellowred", responseKey: "arrowleft", isCongruent: false },
  { stimulusId: "redyellow", responseKey: "arrowright", isCongruent: false },
  { stimulusId: "redgreen", responseKey: "arrowup", isCongruent: false },
  { stimulusId: "redblue", responseKey: "arrowdown", isCongruent: false },
  { stimulusId: "redred", responseKey: "arrowleft", isCongruent: true },
  { stimulusId: "greenyellow", responseKey: "arrowright", isCongruent: false },
  { stimulusId: "greengreen", responseKey: "arrowup", isCongruent: true },
  { stimulusId: "greenblue", responseKey: "arrowdown", isCongruent: false },
  { stimulusId: "greenred", responseKey: "arrowleft", isCongruent: false },
  { stimulusId: "blueyellow", responseKey: "arrowright", isCongruent: false },
  { stimulusId: "bluegreen", responseKey: "arrowup", isCongruent: false },
  { stimulusId: "blueblue", responseKey: "arrowdown", isCongruent: true },
  { stimulusId: "bluered", responseKey: "arrowleft", isCongruent: false },
];

const TASK_PARAMS = {
  fixationDuration: 500,
  postFixationBlankDuration: 200,
  stimulusTimeout: 2000,
  feedbackDuration: 500,
  numTrialsInBlock: 48,
  interTrialInterval: 300,
  taskDuration: 120000,
};

export default function StroopTask() {
  const [stage, setStage] = useState("initialInput");
  const [subjectId, setSubjectId] = useState("");
  const [visitType, setVisitType] = useState("");
  const [error, setError] = useState("");
  const [currentTrialIdx, setCurrentTrialIdx] = useState(0);
  const [trialList, setTrialList] = useState([]);
  const [results, setResults] = useState([]);
  const [displayContent, setDisplayContent] = useState({
    text: "",
    className: "",
  });
  const [stressRating, setStressRating] = useState(null);
  const [summaryStats, setSummaryStats] = useState({});

  const trialStartTime = useRef(0);

  //These variables are used to time the test out after 2 mins and deactivate other hooks after 2 mins
  const timeoutRef = useRef(null);
  const taskTimerRef = useRef(null);
  const responseHandlerRef = useRef(null);
  const taskActiveRef = useRef(false);

  const getFormattedDate = () => {
    const today = new Date();
    return `${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}${today.getFullYear()}`;
  };

  const convertToCSV = (dataArray, isSummary = false) => {
    if (!dataArray || dataArray.length === 0) return "";
    let headers;
    let csvRows = [];
    if (isSummary) {
      headers = [
        "subjectId",
        "visitType",
        "overallAccuracy",
        "totalCorrect",
        "totalTrials",
        "overallMeanRT",
        "congruentMeanRT",
        "incongruentMeanRT",
        "stroopEffect",
        "stressRating",
      ];
      csvRows.push(headers.join(","));
      const values = headers.map((header) => {
        let val;
        if (header === "subjectId") val = subjectId;
        else if (header === "visitType") val = visitType;
        else if (header === "stressRating")
          val = stressRating !== null ? stressRating : "";
        else
          val = summaryStats[header] !== undefined ? summaryStats[header] : "";
        const escaped = ("" + val).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
    } else {
      const desiredHeaders = [
        "subjectId",
        "visitType",
        "stressRating",
        "blockName",
        "trialNum",
        "conditionStr",
        "stimulusWord",
        "stimulusInkName",
        "tableRow",
        "key_pressed",
        "status",
        "rt",
        "isCongruent",
      ];
      const actualDataHeaders = Object.keys(dataArray[0] || {});
      headers = [...new Set([...desiredHeaders, ...actualDataHeaders])];
      csvRows.push(headers.join(","));
      for (const row of dataArray) {
        const values = headers.map((header) => {
          let val;
          if (header === "subjectId") val = subjectId;
          else if (header === "visitType") val = visitType;
          else if (header === "stressRating")
            val = stressRating !== null ? stressRating : "";
          else
            val =
              row[header] === undefined || row[header] === null
                ? ""
                : row[header];
          const escaped = ("" + val).replace(/"/g, '""');
          return `"${escaped}"`;
        });
        csvRows.push(values.join(","));
      }
    }
    return csvRows.join("\n");
  };

  const downloadFile = (csvData, filename) => {
    if (!csvData || !csvData.includes("\n") || csvData.split("\n").length < 2)
      return;
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const sendDataToServer = async () => {
    const payload = {
      metadata: {
        subjectId,
        visitType,
        stressRating,
        testDate: getFormattedDate(),
        timestamp: new Date().toISOString(),
      },
      summary: summaryStats,
      trials: results,
    };

    try {
      const response = await api({
        method: "post",
        url: "/Task/Stroop/Submit",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Data successfully saved to server.");
      alert("Data submitted successfully!");
    } catch (error) {
      if (error.response) {
        const message = error.response.data;
        if (message) {
          alert(message);
        } else {
          alert("Server error");
        }
      } else {
        alert("Network error. Please check your connection.");
      }
    }
  };

  const downloadDataAsCSV = () => {
    const filename = `${subjectId}_Stroop_${getFormattedDate()}_${visitType}_TrialData.csv`;
    downloadFile(convertToCSV([...results], false), filename);
  };

  const downloadSummaryDataAsCSV = () => {
    const filename = `${subjectId}_Stroop_${getFormattedDate()}_${visitType}_Summary.csv`;
    downloadFile(convertToCSV([summaryStats], true), filename);
  };

  // --- Experiment Logic ---
  const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

  const startExperiment = () => {
    if (!subjectId || !visitType) {
      setError("Fields required");
      return;
    }
    generateTrials();
    setStage("instruction1");
  };

  const generateTrials = () => {
    let list = [];
    while (list.length < TASK_PARAMS.numTrialsInBlock)
      list = [...list, ...shuffle(STROOP_TABLE_RAW)];
    setTrialList(list.slice(0, TASK_PARAMS.numTrialsInBlock));
  };

  const parseStimulus = (stimId) => {
    const colors = ["red", "green", "blue", "yellow"];
    const word = colors.find((c) => stimId.startsWith(c));
    const ink = stimId.substring(word.length);
    const key = `arrow${ink === "red" ? "left" : ink === "green" ? "up" : ink === "blue" ? "down" : "right"}`;
    return {
      word: word.toUpperCase(),
      colorClass: RESPONSE_MAP[key].cssClass,
      inkName: ink,
    };
  };

  useEffect(() => {
    const handleKey = (e) => {
      const elem = document.documentElement;
      if (elem.requestFullscreen) elem.requestFullscreen().catch(() => {});
      if (stage === "instruction1") setStage("instruction2");
      else if (stage === "instruction2") setStage("instruction3");
      else if (stage === "instruction3") setStage("task");
    };
    if (stage.startsWith("instruction"))
      window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [stage]);

  const completeTask = () => {
    if (!taskActiveRef.current) return;

    taskActiveRef.current = false;
    clearTimeout(taskTimerRef.current);
    clearTimeout(timeoutRef.current);
    if (responseHandlerRef.current) {
      window.removeEventListener("keydown", responseHandlerRef.current);
      responseHandlerRef.current = null;
    }
    setStage("endTaskScreen");
  };

  useEffect(() => {
    if (stage !== "task") return;

    taskActiveRef.current = true;
    taskTimerRef.current = setTimeout(completeTask, TASK_PARAMS.taskDuration);

    return () => {
      taskActiveRef.current = false;
      clearTimeout(taskTimerRef.current);
      clearTimeout(timeoutRef.current);
      if (responseHandlerRef.current) {
        window.removeEventListener("keydown", responseHandlerRef.current);
        responseHandlerRef.current = null;
      }
    };
  }, [stage]);

  useEffect(() => {
    if (stage === "task") runTrial();
  }, [stage, currentTrialIdx]);

  useEffect(() => {
    if (stage !== "endTaskScreen") return;
    const endTaskTimer = setTimeout(() => setStage("stressQuestion"), 1500);
    return () => clearTimeout(endTaskTimer);
  }, [stage]);

  const runTrial = async () => {
    if (currentTrialIdx >= TASK_PARAMS.numTrialsInBlock) {
      completeTask();
      return;
    }
    if (!taskActiveRef.current) return;
    const trialData = trialList[currentTrialIdx];
    const { word, colorClass, inkName } = parseStimulus(trialData.stimulusId);

    setDisplayContent({ text: "", className: "" });
    await new Promise((r) => setTimeout(r, TASK_PARAMS.interTrialInterval));
    if (!taskActiveRef.current) return;
    setDisplayContent({ text: "+", className: "text-9xl text-white" });
    await new Promise((r) => setTimeout(r, TASK_PARAMS.fixationDuration));
    if (!taskActiveRef.current) return;
    setDisplayContent({ text: "", className: "" });
    await new Promise((r) =>
      setTimeout(r, TASK_PARAMS.postFixationBlankDuration),
    );
    if (!taskActiveRef.current) return;
    setDisplayContent({
      text: word,
      className: `text-8xl font-bold ${colorClass}`,
    });

    trialStartTime.current = performance.now();
    const handleResponse = (e) => {
      const key = e.key.toLowerCase();
      if (["arrowleft", "arrowup", "arrowdown", "arrowright"].includes(key)) {
        window.removeEventListener("keydown", handleResponse);
        responseHandlerRef.current = null;
        clearTimeout(timeoutRef.current);
        const rt = performance.now() - trialStartTime.current;
        finishTrial(key, trialData, word, inkName, rt);
      }
    };
    responseHandlerRef.current = handleResponse;
    window.addEventListener("keydown", handleResponse);
    timeoutRef.current = setTimeout(() => {
      window.removeEventListener("keydown", handleResponse);
      responseHandlerRef.current = null;
      finishTrial(null, trialData, word, inkName, TASK_PARAMS.stimulusTimeout);
    }, TASK_PARAMS.stimulusTimeout);
  };

  const finishTrial = async (key, trialData, word, inkName, rt) => {
    if (!taskActiveRef.current) return;
    const status =
      key === trialData.responseKey
        ? "CORRECT"
        : !key
          ? "TIMEOUT"
          : "INCORRECT";
    setResults((prev) => [
      ...prev,
      {
        blockName: "training",
        trialNum: currentTrialIdx + 1,
        conditionStr: trialData.stimulusId, // Simplified for row tracking
        stimulusWord: word,
        stimulusInkName: inkName,
        key_pressed: key || "TIMEOUT",
        status: status,
        rt: Math.round(rt),
        isCongruent: trialData.isCongruent,
      },
    ]);

    const fbText =
      status === "CORRECT"
        ? "Correct"
        : status === "TIMEOUT"
          ? "Too Slow"
          : "Incorrect";
    setDisplayContent({
      text: fbText,
      className: `text-6xl ${status === "CORRECT" ? "text-sky-400" : "text-orange-500"}`,
    });
    await new Promise((r) => setTimeout(r, TASK_PARAMS.feedbackDuration));
    if (!taskActiveRef.current) return;
    setCurrentTrialIdx((prev) => prev + 1);
  };

  // --- Finalization ---
  useEffect(() => {
    if (stage === "feedback") {
      const correct = results.filter((r) => r.status === "CORRECT");
      const avg = (arr) =>
        arr.length
          ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length)
          : 0;

      const stats = {
        overallAccuracy: ((correct.length / results.length) * 100).toFixed(1),
        totalCorrect: correct.length,
        totalTrials: results.length,
        overallMeanRT: avg(correct.map((r) => r.rt)),
        congruentMeanRT: avg(
          correct.filter((r) => r.isCongruent).map((r) => r.rt),
        ),
        incongruentMeanRT: avg(
          correct.filter((r) => !r.isCongruent).map((r) => r.rt),
        ),
      };
      stats.stroopEffect = stats.incongruentMeanRT - stats.congruentMeanRT;
      setSummaryStats(stats);
    }
  }, [stage]);

  useEffect(() => {
    if (stage === "feedback" && Object.keys(summaryStats).length > 0) {
      downloadDataAsCSV();
      downloadSummaryDataAsCSV();
    }
  }, [summaryStats]);

  return (
    <TailwindInjector>
      <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 font-sans text-gray-800">
        <div className="w-full max-w-4xl flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Stroop Task</h1>
          <div className="text-gray-600">
            {subjectId && `ID: ${subjectId} | Visit: ${visitType}`}
          </div>
        </div>

        <main className="w-full flex-grow flex flex-col items-center justify-center">
          {stage === "initialInput" && (
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md space-y-4 text-center">
              <input
                type="text"
                placeholder="Subject ID"
                className="border p-2 w-full rounded"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
              />
              <select
                className="border p-2 w-full rounded"
                value={visitType}
                onChange={(e) => setVisitType(e.target.value)}
              >
                <option value="">-- Select Visit --</option>
                <option value="Baseline">Baseline</option>
                <option value="Post-training">Post-training</option>
                <option value="Follow-up">Follow-up</option>
              </select>
              <button
                onClick={startExperiment}
                className="bg-blue-500 text-white px-6 py-2 rounded"
              >
                Start
              </button>
            </div>
          )}

          {[
            "instruction1",
            "instruction2",
            "instruction3",
            "task",
            "endTaskScreen",
          ].includes(stage) && (
            <div className="w-full max-w-5xl h-[70vh] bg-black rounded-lg flex flex-col items-center justify-center p-8 text-white text-center overflow-y-auto">
              {stage === "instruction1" && (
                <div
                  dangerouslySetInnerHTML={{ __html: MESSAGES.instruction1 }}
                  className="text-2xl"
                />
              )}
              {stage === "instruction2" && (
                <div
                  dangerouslySetInnerHTML={{ __html: MESSAGES.instruction2 }}
                  className="text-2xl"
                />
              )}
              {stage === "instruction3" && (
                <div className="text-left max-w-2xl">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: MESSAGES.instruction3_examples,
                    }}
                  />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: MESSAGES.instruction3_text,
                    }}
                  />
                </div>
              )}
              {(stage === "task" || stage === "endTaskScreen") && (
                <div className={displayContent.className}>
                  {displayContent.text ||
                    (stage === "endTaskScreen" && "Task Complete!")}
                </div>
              )}
            </div>
          )}

          {stage === "stressQuestion" && (
            <div className="bg-black text-white p-12 rounded-lg text-center w-full max-w-4xl">
              <p className="text-xl mb-6">
                How stressful was this activity? (0-10)
              </p>
              <div className="flex justify-center flex-wrap gap-2 mb-8">
                {[...Array(11).keys()].map((i) => (
                  <label
                    key={i}
                    className="cursor-pointer hover:bg-gray-700 p-2 rounded"
                  >
                    <input
                      type="radio"
                      name="stress"
                      onChange={() => setStressRating(i)}
                      className="mr-1 accent-sky-400"
                    />
                    {i}
                  </label>
                ))}
              </div>
              <button
                className="bg-blue-500 px-8 py-3 rounded"
                onClick={() =>
                  stressRating !== null
                    ? setStage("feedback")
                    : alert("Select a rating")
                }
              >
                Submit
              </button>
            </div>
          )}

          {stage === "feedback" && (
            <div className="bg-white p-8 border rounded-lg w-full max-w-2xl shadow-sm text-center">
              <h2 className="text-2xl font-bold mb-4">Results</h2>
              <div className="text-left space-y-2 mb-6">
                <p>Accuracy: {summaryStats.overallAccuracy}%</p>
                <p>Mean RT: {summaryStats.overallMeanRT} ms</p>
                <p>Stroop Effect: {summaryStats.stroopEffect} ms</p>
              </div>
              <button
                onClick={sendDataToServer}
                className="bg-green-500 text-white px-4 py-2 rounded m-2"
              >
                Submit
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-500 text-white px-4 py-2 rounded m-2"
              >
                Restart
              </button>
              <button
                onClick={() => (window.location.href = "/dashboard/subject")}
                className="w-full bg-red-600 text-white p-4 rounded-xl font-bold shadow-md hover:bg-green-700 transition"
              >
                Go Back
              </button>
            </div>
          )}

          {stage === "task" && (
            <div className="w-full max-w-5xl mt-4 bg-gray-200 rounded-full h-4">
              <div
                className="bg-green-500 h-full transition-all"
                style={{
                  width: `${(currentTrialIdx / TASK_PARAMS.numTrialsInBlock) * 100}%`,
                }}
              />
            </div>
          )}
        </main>
      </div>
    </TailwindInjector>
  );
}
